import { formatHour12 } from './availabilitySummary';
import {
  TALKER_WEEKDAYS,
  hhmmFromMinutes,
  minutesFromHhmm,
  normalizeTalkerAvailability,
} from './talkerAvailability';

export const LISTENER_WEEKDAYS = TALKER_WEEKDAYS;

export const DEFAULT_LISTENER_SLOT = { startTime: '09:00', endTime: '17:00' };

const TZ_LABELS = {
  'America/New_York': 'Eastern Time',
  'America/Detroit': 'Eastern Time',
  'America/Kentucky/Louisville': 'Eastern Time',
  'America/Indiana/Indianapolis': 'Eastern Time',
  'America/Chicago': 'Central Time',
  'America/Denver': 'Mountain Time',
  'America/Phoenix': 'Mountain Time',
  'America/Los_Angeles': 'Pacific Time',
  'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time',
  'Etc/UTC': 'UTC',
  UTC: 'UTC',
};

let slotSeq = 0;
const nextSlotId = () => {
  slotSeq += 1;
  return `slot-${slotSeq}`;
};

const asEnd = value => (value === '00:00' ? '24:00' : value);

/**
 * 30-minute clock options for the weekly hours editor.
 *
 * @param {boolean} [isEnd]
 * @returns {Array<{ value: string, label: string }>}
 */
export const listenerHourOptions = (isEnd = false) => {
  const start = isEnd ? 30 : 0;
  const stop = isEnd ? 24 * 60 : 23 * 60 + 30;
  const hours = [];
  for (let minutes = start; minutes <= stop; minutes += 30) {
    const value = minutes === 24 * 60 ? '24:00' : hhmmFromMinutes(minutes);
    hours.push({ value, label: formatHour12(value) });
  }
  return hours;
};

/**
 * Friendly marketplace timezone label (Eastern Time, not America/New_York).
 *
 * @param {string} [timeZone]
 * @returns {string}
 */
export const listenerTimeZoneLabel = (timeZone = '') => {
  const zone = typeof timeZone === 'string' ? timeZone.trim() : '';
  if (!zone) {
    return 'Eastern Time';
  }
  if (TZ_LABELS[zone]) {
    return TZ_LABELS[zone];
  }
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longGeneric',
    }).formatToParts(new Date());
    const name = parts.find(part => part.type === 'timeZoneName')?.value;
    if (name && name !== zone) {
      return name;
    }
  } catch (e) {
    // Fall through to the raw IANA name.
  }
  return zone;
};

const emptyDay = () => ({ on: false, slots: [] });

/**
 * Join touching windows (12:00–21:00 + 21:00–23:00 → 12:00–23:00).
 *
 * @param {Array} [entries]
 * @returns {Array}
 */
export const mergeAdjacentListenerEntries = (entries = []) => {
  return LISTENER_WEEKDAYS.flatMap(day => {
    const list = (entries || [])
      .filter(entry => entry?.dayOfWeek === day && entry.startTime && entry.endTime)
      .map(entry => ({
        start: minutesFromHhmm(entry.startTime),
        end: minutesFromHhmm(asEnd(entry.endTime)),
        startTime: entry.startTime,
        endTime: asEnd(entry.endTime),
      }))
      .filter(entry => Number.isFinite(entry.start) && Number.isFinite(entry.end))
      .sort((a, b) => a.start - b.start || a.end - b.end);
    const merged = [];
    list.forEach(slot => {
      const last = merged[merged.length - 1];
      if (last && slot.start <= last.end) {
        if (slot.end > last.end) {
          last.end = slot.end;
          last.endTime = slot.endTime;
        }
        return;
      }
      merged.push({ ...slot });
    });
    return merged.map(slot => ({
      dayOfWeek: day,
      startTime: slot.startTime,
      endTime: slot.endTime === '24:00' ? '00:00' : slot.endTime,
      seats: 1,
    }));
  });
};

/**
 * Editor day map from a listing weekly plan. Adjacent windows become one slot.
 *
 * @param {Object} [plan]
 * @returns {Object}
 */
export const daysFromListenerPlan = (plan = {}) => {
  const normalized = normalizeTalkerAvailability({
    ...plan,
    entries: mergeAdjacentListenerEntries(plan.entries || []),
  });
  const byDay = Object.fromEntries(LISTENER_WEEKDAYS.map(day => [day, emptyDay()]));
  normalized.entries.forEach(entry => {
    const startTime = entry.startTime;
    const endTime = asEnd(entry.endTime);
    if (!byDay[entry.dayOfWeek]) {
      return;
    }
    byDay[entry.dayOfWeek].on = true;
    byDay[entry.dayOfWeek].slots.push({
      id: nextSlotId(),
      startTime,
      endTime,
    });
  });
  return byDay;
};

export const entriesFromListenerDays = (days = {}) =>
  mergeAdjacentListenerEntries(
    LISTENER_WEEKDAYS.flatMap(day => {
      const row = days[day];
      if (!row?.on) {
        return [];
      }
      return (row.slots || [])
        .filter(slot => slot?.startTime && slot?.endTime)
        .map(slot => ({
          dayOfWeek: day,
          startTime: slot.startTime,
          endTime: slot.endTime,
          seats: 1,
        }));
    })
  );

const sortedSlotBounds = slots =>
  (slots || [])
    .map(slot => ({
      start: minutesFromHhmm(slot.startTime),
      end: minutesFromHhmm(asEnd(slot.endTime)),
    }))
    .filter(bound => Number.isFinite(bound.start) && Number.isFinite(bound.end))
    .sort((a, b) => a.start - b.start || a.end - b.end);

/**
 * First non-overlapping default window. 9 AM–5 PM when the day is empty.
 *
 * @param {Array<{ startTime: string, endTime: string }>} [slots]
 * @returns {{ startTime: string, endTime: string }}
 */
export const nextListenerSlot = (slots = []) => {
  if (!slots.length) {
    return { ...DEFAULT_LISTENER_SLOT };
  }
  const bounds = sortedSlotBounds(slots);
  const lastEnd = bounds[bounds.length - 1]?.end;
  if (Number.isFinite(lastEnd) && lastEnd + 120 <= 24 * 60) {
    return {
      startTime: hhmmFromMinutes(lastEnd),
      endTime: hhmmFromMinutes(lastEnd + 120),
    };
  }
  for (let start = 0; start <= 22 * 60; start += 30) {
    const end = start + 120;
    const overlaps = bounds.some(bound => start < bound.end && end > bound.start);
    if (!overlaps && end <= 24 * 60) {
      return {
        startTime: hhmmFromMinutes(start),
        endTime: hhmmFromMinutes(end),
      };
    }
  }
  return { startTime: '22:00', endTime: '24:00' };
};

/**
 * Copy one day's windows onto every weekday. Local state only.
 *
 * @param {Object} days
 * @param {string} [sourceDay]
 * @returns {Object}
 */
export const listenerHoursSourceDay = (days = {}) =>
  LISTENER_WEEKDAYS.find(day => days[day]?.on && days[day]?.slots?.length) || LISTENER_WEEKDAYS[0];

/**
 * First slot of the source day as a 12-hour range, e.g. "9 AM–5 PM".
 *
 * @param {Object} [days]
 * @param {string} [sourceDay]
 * @returns {string}
 */
const compactHour12 = hhmm => formatHour12(hhmm).replace(':00 ', ' ');

export const listenerHoursRangeLabel = (days = {}, sourceDay) => {
  const key = sourceDay || listenerHoursSourceDay(days);
  const slot = days[key]?.slots?.[0] || DEFAULT_LISTENER_SLOT;
  return `${compactHour12(slot.startTime || DEFAULT_LISTENER_SLOT.startTime)}–${compactHour12(
    slot.endTime || DEFAULT_LISTENER_SLOT.endTime
  )}`;
};

export const copyListenerHoursToAllDays = (days = {}, sourceDay) => {
  const sourceKey = sourceDay || listenerHoursSourceDay(days);
  const sourceSlots = (days[sourceKey]?.slots || []).map(slot => ({
    startTime: slot.startTime || DEFAULT_LISTENER_SLOT.startTime,
    endTime: slot.endTime || DEFAULT_LISTENER_SLOT.endTime,
  }));
  const slots = sourceSlots.length ? sourceSlots : [{ ...DEFAULT_LISTENER_SLOT }];
  return Object.fromEntries(
    LISTENER_WEEKDAYS.map(day => [
      day,
      {
        on: true,
        slots: slots.map(slot => ({ ...slot, id: nextSlotId() })),
      },
    ])
  );
};

/**
 * Copy one day's windows onto Monday–Friday. Weekend rows stay as they were.
 *
 * @param {Object} days
 * @param {string} [sourceDay]
 * @returns {Object}
 */
export const copyListenerHoursToWeekdays = (days = {}, sourceDay) => {
  const all = copyListenerHoursToAllDays(days, sourceDay);
  return {
    ...days,
    mon: all.mon,
    tue: all.tue,
    wed: all.wed,
    thu: all.thu,
    fri: all.fri,
  };
};

const slotKey = slot => `${slot.startTime}|${asEnd(slot.endTime)}`;

/**
 * Validate one day's windows.
 *
 * @param {Array<{ startTime: string, endTime: string }>} [slots]
 * @returns {{ ok: boolean, reason: 'end'|'overlap'|'duplicate'|null }}
 */
export const validateListenerDaySlots = (slots = []) => {
  const bounds = (slots || []).map(slot => ({
    start: minutesFromHhmm(slot.startTime),
    end: minutesFromHhmm(asEnd(slot.endTime)),
    key: slotKey(slot),
  }));
  if (bounds.some(bound => !Number.isFinite(bound.start) || !Number.isFinite(bound.end))) {
    return { ok: false, reason: 'end' };
  }
  if (bounds.some(bound => bound.end <= bound.start)) {
    return { ok: false, reason: 'end' };
  }
  const keys = bounds.map(bound => bound.key);
  if (new Set(keys).size !== keys.length) {
    return { ok: false, reason: 'duplicate' };
  }
  const ordered = [...bounds].sort((a, b) => a.start - b.start || a.end - b.end);
  for (let i = 1; i < ordered.length; i += 1) {
    if (ordered[i].start < ordered[i - 1].end) {
      return { ok: false, reason: 'overlap' };
    }
  }
  return { ok: true, reason: null };
};

/**
 * Validate the whole week. Returns the first problem day.
 *
 * @param {Object} days
 * @returns {{ ok: boolean, day: string|null, reason: string|null }}
 */
export const validateListenerHours = (days = {}) => {
  for (const day of LISTENER_WEEKDAYS) {
    const row = days[day];
    if (!row?.on) {
      continue;
    }
    const result = validateListenerDaySlots(row.slots);
    if (!result.ok) {
      return { ok: false, day, reason: result.reason };
    }
  }
  return { ok: true, day: null, reason: null };
};

/**
 * True when every day is a leftover 00:00–00:00 24/7 overlay.
 *
 * @param {Object} [plan]
 * @returns {boolean}
 */
export const isAvailableNowLivePlan = (plan = {}) => {
  const entries = Array.isArray(plan.entries) ? plan.entries : [];
  if (entries.length < 7) {
    return false;
  }
  const days = new Set(entries.map(entry => entry?.dayOfWeek));
  if (days.size < 7) {
    return false;
  }
  return entries.every(entry => {
    const start = entry?.startTime || entry?.start;
    const end = asEnd(entry?.endTime || entry?.end);
    return start === '00:00' && (end === '00:00' || end === '24:00');
  });
};

const isOpenSeat = entry => entry?.seats == null || entry.seats > 0;

const isAllDayWindow = entry => {
  const start = entry?.startTime || entry?.start;
  const end = asEnd(entry?.endTime || entry?.end);
  return start === '00:00' && (end === '00:00' || end === '24:00');
};

/**
 * Weekday key (mon…sun) for a calendar date in the listing timezone.
 *
 * @param {Date} date
 * @param {string} [timeZone]
 * @returns {string}
 */
export const weekdayKeyFromDate = (date, timeZone) => {
  try {
    const label = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      timeZone: timeZone || 'UTC',
    }).format(date);
    return label.slice(0, 3).toLowerCase();
  } catch (e) {
    return LISTENER_WEEKDAYS[0];
  }
};

/**
 * One day's bookable hours from a weekly plan. Adjacent windows are merged.
 * "All day" only for a true 00:00–24:00 window.
 *
 * @param {Object} [plan]
 * @param {string} dayOfWeek
 * @returns {{ kind: 'off'|'allDay'|'hours', labels: string[] }}
 */
export const formatListenerDayHours = (plan = {}, dayOfWeek) => {
  const entries = mergeAdjacentListenerEntries(
    (Array.isArray(plan.entries) ? plan.entries : []).filter(
      entry => entry?.dayOfWeek === dayOfWeek && isOpenSeat(entry)
    )
  );
  if (!entries.length) {
    return { kind: 'off', labels: [] };
  }
  if (entries.length === 1 && isAllDayWindow(entries[0])) {
    return { kind: 'allDay', labels: [] };
  }
  return {
    kind: 'hours',
    labels: entries.map(entry => {
      const start = formatHour12(entry.startTime);
      const end = formatHour12(asEnd(entry.endTime));
      return `${start} – ${end}`;
    }),
  };
};
